import { GameZoneSheet } from '@/components/molecules/GameZoneModal/GameZoneSheet'
import React from 'react'
import {
  useBreakpointValue,
  Box,
  CloseButton,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'

export type GameZoneModalVariant = 'entry' | 'switch' | 'deposit' | 'withdraw'

type GameZoneModalProps = {
  variant: GameZoneModalVariant
  title: string
  description: string
  descriptionColor?: string
  onClose: () => void
  children: React.ReactNode
  isBusy?: boolean
  isClosable?: boolean
  showClose?: boolean
  embedded?: boolean
}

const variantStyles: Record<
  GameZoneModalVariant,
  { maxW: string; bg: string; padding: { base: number; sm: number }; spacing: number }
> = {
  entry: { maxW: '560px', bg: 'ninja.600', padding: { base: 5, sm: 7 }, spacing: 4 },
  switch: { maxW: '560px', bg: 'ninja.600', padding: { base: 5, sm: 7 }, spacing: 4 },
  deposit: { maxW: '560px', bg: 'ninja.600', padding: { base: 5, sm: 7 }, spacing: 5 },
  withdraw: { maxW: '560px', bg: 'ninja.600', padding: { base: 5, sm: 7 }, spacing: 5 },
}

export const GameZoneModal: React.FC<GameZoneModalProps> = ({
  variant,
  title,
  description,
  descriptionColor = 'ninja.200',
  onClose,
  children,
  isBusy = false,
  isClosable = true,
  showClose = true,
  embedded = false,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const styles = variantStyles[variant]
  const content = (
    <VStack align="stretch" spacing={styles.spacing}>
      <HStack justify="space-between" align="flex-start" minH="62px">
        <VStack align="flex-start" spacing={2} flex={1} minW={0}>
          <Text fontSize="20px" fontWeight={700} lineHeight={1} letterSpacing="-0.4px">
            {title}
          </Text>
          <Text color={descriptionColor} fontSize="14px" fontWeight={500} lineHeight={1.25}>
            {description}
          </Text>
        </VStack>
        {showClose && embedded && <CloseButton size="sm" flexShrink={0} isDisabled={isBusy} onClick={onClose} />}
        {showClose && !embedded && (
          <ModalCloseButton position="static" size="sm" flexShrink={0} isDisabled={isBusy} onClick={onClose} />
        )}
      </HStack>
      {children}
    </VStack>
  )

  if (isMobile) {
    return (
      <GameZoneSheet onClose={onClose} isBusy={isBusy} isClosable={isClosable}>
        {content}
      </GameZoneSheet>
    )
  }

  if (embedded) {
    return (
      <Flex
        position="absolute"
        inset={0}
        zIndex={20}
        justify="center"
        align="flex-start"
        overflowY="auto"
        overscrollBehavior="contain"
        p={{ base: 3, md: 6 }}
        bg="rgba(4, 3, 24, 0.82)"
        backdropFilter="blur(6px)"
      >
        <Box
          w="full"
          maxW={styles.maxW}
          my="auto"
          flexShrink={0}
          p={styles.padding}
          pos="relative"
          bg={styles.bg}
          color="white"
          borderRadius="20px"
          boxShadow="0 20px 47px rgba(0, 0, 0, 0.25)"
        >
          {content}
        </Box>
      </Flex>
    )
  }

  return (
    <Modal
      isOpen
      isCentered
      onClose={onClose}
      closeOnEsc={isClosable && !isBusy}
      closeOnOverlayClick={isClosable && !isBusy}
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        w="calc(100% - 32px)"
        maxW={styles.maxW}
        m={4}
        p={styles.padding}
        bg={styles.bg}
        color="white"
        borderRadius="20px"
        boxShadow="0 20px 47px rgba(0, 0, 0, 0.25)"
        overflow="visible"
      >
        <ModalBody p={0}>{content}</ModalBody>
      </ModalContent>
    </Modal>
  )
}
